import DownloadButton from "../DownloadButton";
import ProfilePlaceholder from "../../assets/svgIcons/ProfilePlaceholder.svg";
import "../../styles/components/transactionPrintLayouts/POInvoiceLayout.css";
import "../../styles/components/transactionPrintLayouts/FinalInspectionInvoiceLayout.css";

export const FinalInspectionInvoiceLayout = ({
  headDetails,
  companyData,
  itemDetails,
  invoiceTableData,
  headFields,
}) => {
  const organizationData =
    JSON.parse(localStorage.getItem("organisation")) || {};

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    if (isNaN(d)) return dateStr;
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    return `${day}-${month}-${d.getFullYear()}`;
  };

  const rows = Array.isArray(itemDetails) ? itemDetails : [];
  const headingFields = Array.isArray(invoiceTableData?.headings)
    ? invoiceTableData.headings
    : [];
  const transactionHeadFields = Array.isArray(headFields) ? headFields : [];

  const getHeadField = (details, ...keys) => {
    for (const key of keys) {
      const value = details?.[key];
      if (value !== null && value !== undefined && value !== "") return value;
    }
    return "";
  };

  const getField = (row, ...keys) => {
    for (const key of keys) {
      const value = row?.[key];
      if (value !== null && value !== undefined && value !== "") return value;
    }
    return "";
  };

  const normalizeFieldName = (value) =>
    String(value || "")
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");

  const findHeadingFieldName = (...possibleNames) => {
    const normalizedCandidates = possibleNames.map((name) =>
      normalizeFieldName(name),
    );
    const matched = headingFields.find((field) => {
      const normalizedFieldName = normalizeFieldName(field?.field_name);
      const normalizedDisplayName = normalizeFieldName(field?.display_name);
      return normalizedCandidates.some(
        (candidate) =>
          candidate &&
          (candidate === normalizedFieldName ||
            candidate === normalizedDisplayName),
      );
    });
    return matched?.field_name;
  };

  const toTitleCase = (value) =>
    String(value || "")
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");

  const deriveInspectionLabel = () => {
    const transactionName =
      getHeadField(
        headDetails,
        "transaction_name",
        "transaction",
        "report_name",
        "menu_name",
      ) || "";

    if (transactionName.trim()) {
      const normalized = toTitleCase(transactionName.replace(/[_-]+/g, " "));
      return /\bno\b/i.test(normalized) ? normalized : `${normalized} No`;
    }

    const path = window.location.pathname.split("/").filter(Boolean);
    const transactionSegment = path[1] || "";
    const fromPath = toTitleCase(transactionSegment.replace(/[-_]+/g, " "));
    return fromPath ? `${fromPath} No` : "Inspection No";
  };

  const serialNumberField = findHeadingFieldName(
    "sl_no",
    "si_no",
    "serial_no",
    "serial_number",
    "s_no",
  );
  const parameterField = findHeadingFieldName("parameter", "characteristics");
  const specificationField = findHeadingFieldName(
    "specification",
    "parameter_value",
    "spec",
  );
  const toleranceField = findHeadingFieldName("tolerance", "tolerence");
  const methodField = findHeadingFieldName(
    "method_of_insp",
    "inspection_method",
    "method",
  );
  const inspectionNumberField = transactionHeadFields.find((field) => {
    const fieldName = normalizeFieldName(field?.field_name);
    const displayName = normalizeFieldName(field?.display_name);
    const title = normalizeFieldName(field?.title);

    return (
      fieldName === "tranno" ||
      fieldName === "transactionno" ||
      fieldName === "transactionnumber" ||
      displayName.includes("inspectionno") ||
      title.includes("inspectionno")
    );
  });

  const inspectionNumberLabel =
    inspectionNumberField?.display_name ||
    inspectionNumberField?.title ||
    deriveInspectionLabel();

  const observationReadingNumbers = [
    1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20,
  ];

  const FINAL_FIRST_PAGE_ROWS = 17;
  const FINAL_NEXT_PAGE_ROWS = 20;

  const getRowChunks = (sourceRows) => {
    if (!Array.isArray(sourceRows) || sourceRows.length === 0) return [[]];

    const chunks = [];
    chunks.push(sourceRows.slice(0, FINAL_FIRST_PAGE_ROWS));

    let cursor = FINAL_FIRST_PAGE_ROWS;
    while (cursor < sourceRows.length) {
      chunks.push(sourceRows.slice(cursor, cursor + FINAL_NEXT_PAGE_ROWS));
      cursor += FINAL_NEXT_PAGE_ROWS;
    }

    return chunks;
  };

  const rowChunks = getRowChunks(rows);

  const getReadingValue = (row, readingNumber) => {
    const resolvedReadingField = findHeadingFieldName(
      String(readingNumber),
      `obv_${readingNumber}`,
      `obs_${readingNumber}`,
      `value_${readingNumber}`,
      `reading_${readingNumber}`,
      `observation_${readingNumber}`,
    );

    return getField(
      row,
      resolvedReadingField,
      `obv_${readingNumber}`,
      `obs_${readingNumber}`,
      String(readingNumber),
      `value_${readingNumber}`,
      `reading_${readingNumber}`,
    );
  };

  return (
    <div className="invoice-container final-inspection-page-container">
      <div className="final-header-grid">
        <div className="final-header-company-block">
          <div className="invoice-logo-container final-header-logo-container">
            <img
              src={organizationData.logo_url || ProfilePlaceholder}
              alt="Logo"
              className="invoice-logo-content"
            />
          </div>
          <div className="final-header-company-text-wrap">
            <h3 className="invoice-company-name final-company-name">
              {organizationData.name || "N/A"}
            </h3>
            {organizationData.address && (
              <p className="invoice-company-address final-company-address">
                {organizationData.address}
              </p>
            )}
          </div>
        </div>

        <div className="final-header-title-block">
          <h2 className="final-header-title">Final Inspection Report</h2>
        </div>

        <div className="final-header-ref-block">
          <div className="final-header-ref-row">
            <strong className="final-header-ref-label">REF. NO.:</strong>
            <span>
              {getHeadField(
                headDetails,
                "ref_no",
                "ref_number",
                "reference_no",
              ) || "FP/F/QA/04"}
            </span>
          </div>
          <div className="final-header-ref-row">
            <strong className="final-header-ref-label">REV. NO.:</strong>
            <span>{getHeadField(headDetails, "rev_no", "revision_no")}</span>
          </div>
          <div className="final-header-ref-row">
            <strong className="final-header-ref-label">REV. DATE:</strong>
            <span>
              {getHeadField(headDetails, "rev_date", "revision_date")
                ? formatDate(
                    getHeadField(headDetails, "rev_date", "revision_date"),
                  )
                : ""}
            </span>
          </div>
        </div>
      </div>

      <div className="final-meta-row final-meta-row-4">
        <div className="report-header-section">
          <h4 className="invoice-section-title">Work Order No.:</h4>
          <span className="invoice-section-text report-header-text">
            {getHeadField(
              headDetails,
              "workorder_no",
              "wo_no",
              "work_order_no",
            )}
          </span>
        </div>
        <div className="report-header-section">
          <h4 className="invoice-section-title">Part Name:</h4>
          <span className="invoice-section-text report-header-text">
            {getHeadField(
              headDetails,
              "part_name",
              "product_name",
              "product_desc",
            )}
          </span>
        </div>
        <div className="report-header-section">
          <h4 className="invoice-section-title">Lot Qty.:</h4>
          <span className="invoice-section-text report-header-text">
            {getHeadField(headDetails, "lot_qty", "total_quantity", "quantity")}
          </span>
        </div>
        <div className="report-header-section final-no-right-border">
          <h4 className="invoice-section-title">Sample Qty.:</h4>
          <span className="invoice-section-text report-header-text">
            {getHeadField(headDetails, "sample_qty", "sampleQty")}
          </span>
        </div>
      </div>

      <div className="final-meta-row final-meta-row-3 final-meta-row-inline">
        <div className="report-header-section">
          <h4 className="invoice-section-title">{inspectionNumberLabel}:</h4>
          <span className="invoice-section-text report-header-text">
            {getHeadField(headDetails, "tran_no")}
          </span>
        </div>
        <div className="report-header-section">
          <h4 className="invoice-section-title">Part No.:</h4>
          <span className="invoice-section-text report-header-text">
            {getHeadField(headDetails, "part_no", "part_number", "item_code")}
          </span>
        </div>
        <div className="report-header-section final-no-right-border">
          <h4 className="invoice-section-title">Date:</h4>
          <span className="invoice-section-text report-header-text">
            {formatDate(
              getHeadField(
                headDetails,
                "date",
                "created_at",
                "inspection_date",
                "createdAt",
              ),
            )}
          </span>
        </div>
      </div>

      <div className="invoice-overflow-x-scroll final-table-standard">
        <table className="invoice-table">
          <thead className="invoice-thead">
            <tr>
              <th className="invoice-th final-first-col" rowSpan={2}>
                SL NO
              </th>
              <th className="invoice-th" rowSpan={2}>
                Parameter
              </th>
              <th className="invoice-th" rowSpan={2}>
                Specification
              </th>
              <th className="invoice-th" rowSpan={2}>
                Tolerance
              </th>
              <th className="invoice-th" rowSpan={2}>
                Method of Inspection
              </th>
              <th
                className="invoice-th"
                colSpan={observationReadingNumbers.length}
              >
                OBSERVATION READING
              </th>
              <th className="invoice-th final-no-right-border" rowSpan={2}>
                Remarks
              </th>
            </tr>
            <tr>
              {observationReadingNumbers.map((n, idx) => (
                <th
                  key={n}
                  className={`invoice-th ${idx === observationReadingNumbers.length - 1 ? "final-no-right-border" : ""}`}
                >
                  {n}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <tr key={idx}>
                <td className="invoice-td final-first-col">
                  {getField(
                    row,
                    serialNumberField,
                    "sl_no",
                    "si_no",
                    "serial_no",
                    "serial_number",
                    "s_no",
                  ) || idx + 1}
                </td>
                <td className="invoice-td">
                  {getField(
                    row,
                    parameterField,
                    "parameter",
                    "characteristics",
                  )}
                </td>
                <td className="invoice-td">
                  {getField(
                    row,
                    specificationField,
                    "specification",
                    "parameter_value",
                    "spec",
                  )}
                </td>
                <td className="invoice-td">
                  {getField(row, toleranceField, "tolerance", "tolerence")}
                </td>
                <td className="invoice-td">
                  {getField(
                    row,
                    methodField,
                    "method_of_insp",
                    "inspection_method",
                    "method",
                  )}
                </td>
                {observationReadingNumbers.map((readingNum, readingIdx) => (
                  <td
                    key={readingNum}
                    className={`invoice-td ${readingIdx === observationReadingNumbers.length - 1 ? "final-no-right-border" : ""}`}
                  >
                    {getReadingValue(row, readingNum)}
                  </td>
                ))}
                <td className="invoice-td final-no-right-border">
                  {getField(row, "remarks", "remark")}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="final-table-chunked">
        {rowChunks.map((chunkRows, chunkIndex) => (
          <div
            key={`final-table-chunk-${chunkIndex}`}
            className={`invoice-overflow-x-scroll ${
              chunkIndex > 0 ? "final-page-break-chunk" : ""
            }`}
          >
            <table className="invoice-table">
              {chunkIndex === 0 && (
                <thead className="invoice-thead">
                  <tr>
                    <th className="invoice-th final-first-col" rowSpan={2}>
                      SL NO
                    </th>
                    <th className="invoice-th" rowSpan={2}>
                      Parameter
                    </th>
                    <th className="invoice-th" rowSpan={2}>
                      Specification
                    </th>
                    <th className="invoice-th" rowSpan={2}>
                      Tolerance
                    </th>
                    <th className="invoice-th" rowSpan={2}>
                      Method of Inspection
                    </th>
                    <th
                      className="invoice-th"
                      colSpan={observationReadingNumbers.length}
                    >
                      OBSERVATION READING
                    </th>
                    <th className="invoice-th final-no-right-border" rowSpan={2}>
                      Remarks
                    </th>
                  </tr>
                  <tr>
                    {observationReadingNumbers.map((n, idx) => (
                      <th
                        key={n}
                        className={`invoice-th ${idx === observationReadingNumbers.length - 1 ? "final-no-right-border" : ""}`}
                      >
                        {n}
                      </th>
                    ))}
                  </tr>
                </thead>
              )}
              <tbody>
                {chunkRows.map((row, idx) => {
                  const absoluteIndex =
                    rowChunks
                      .slice(0, chunkIndex)
                      .reduce((sum, group) => sum + group.length, 0) + idx;

                  return (
                    <tr key={`${chunkIndex}-${idx}`}>
                      <td className="invoice-td final-first-col">
                        {getField(
                          row,
                          serialNumberField,
                          "sl_no",
                          "si_no",
                          "serial_no",
                          "serial_number",
                          "s_no",
                        ) || absoluteIndex + 1}
                      </td>
                      <td className="invoice-td">
                        {getField(
                          row,
                          parameterField,
                          "parameter",
                          "characteristics",
                        )}
                      </td>
                      <td className="invoice-td">
                        {getField(
                          row,
                          specificationField,
                          "specification",
                          "parameter_value",
                          "spec",
                        )}
                      </td>
                      <td className="invoice-td">
                        {getField(row, toleranceField, "tolerance", "tolerence")}
                      </td>
                      <td className="invoice-td">
                        {getField(
                          row,
                          methodField,
                          "method_of_insp",
                          "inspection_method",
                          "method",
                        )}
                      </td>
                      {observationReadingNumbers.map((readingNum, readingIdx) => (
                        <td
                          key={readingNum}
                          className={`invoice-td ${readingIdx === observationReadingNumbers.length - 1 ? "final-no-right-border" : ""}`}
                        >
                          {getReadingValue(row, readingNum)}
                        </td>
                      ))}
                      <td className="invoice-td final-no-right-border">
                        {getField(row, "remarks", "remark")}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ))}
      </div>

      <div className="final-footer-grid">
        <div className="final-footer-signature-box">
          <strong>Inspected By:</strong>{" "}
          {getHeadField(headDetails, "inspected_by", "inspector", "checked_by")}
        </div>
        <div className="final-footer-signature-box final-no-right-border">
          <strong>Approved By:</strong>{" "}
          {getHeadField(headDetails, "approved_by", "approver", "supervisor")}
        </div>
      </div>

      <p className="invoice-system-generated-note">
        This is a system-generated document and does not require a signature.
      </p>

      <div className="invoice-buttons" style={{ paddingTop: 32 }}>
        <DownloadButton
          tableData={invoiceTableData}
          fileName="final-inspection-report"
          downloadFormats={["excel", "pdf", "full-page-pdf"]}
          fullPageDownload={true}
          fullPageSelector=".final-inspection-page-container"
        />
      </div>
    </div>
  );
};
